<template>
  <div>
    <div class="page-bar">
      <breadcrumb :crumbs="breadcrumbArray" />
    </div>
    <h1 class="page-title">
      Printer Settings
    </h1>
    <div class="note note-info">
      <p>Create and edit printer settings for a store.</p>
    </div>

    <div
      v-if="!activeLocation.id"
      class="alert alert-info center margin-top-20"
    >
      <h4>No Store Selected</h4>
      <p>Please select a store from the location panel on the right to view printer settings for it.</p>
    </div>

    <loading-screen
      :show="loading"
      :color="'#2C3E50'"
      :display="'inline'"
    />

    <div v-if="activeLocation && activeLocation.id && !loading">
      <div class="portlet box blue-hoki light portlet-fit bordered margin-top-20">
        <div class="portlet-title bg-blue-chambray">
          <div
            v-if="settings.id !== undefined"
            class="menu-image-main"
          >
            <img src="@/../public/client_logo.png">
          </div>
          <div class="caption">
            <i
              v-if="settings.id === undefined && can('print_setting create')"
              class="fa fa-2x fa-plus-circle"
            />
            <span class="caption-subject font-default bold uppercase">
              {{ settings.id === undefined && can('print_setting create') ? 'Create' : '' }} Printer Settings
            </span>
            <div class="caption-desc font-grey-cascade">
              {{ settings.id === undefined && can('print_setting create') ? 'Create' : 'Edit' }} printer settings for {{ activeLocation.display_name }}.
            </div>
          </div>
        </div>
        <div class="portlet-body">
          <div class="row">
            <div class="col-md-12">
              <div
                v-show="listErrorMessage"
                ref="listErrorMessage"
                class="alert alert-danger"
              >
                <button
                  class="close"
                  @click="clearError('listErrorMessage')"
                />
                <span>{{ listErrorMessage }}</span>
              </div>
            </div>
          </div>
          <div class="mt-element-list">
            <div class="mt-list-container list-news">
              <no-results
                :show="settings.id === undefined && !can('print_setting create')"
                :type="'printer settings'"
              />
              <form
                v-if="
                  (settings.id === undefined && can('print_setting create')) ||
                    (settings.id !== undefined && can('print_setting read'))
                "
                class="display-inline-flex flex-direction-column"
                @submit.prevent="upsertSettings"
              >
                <div class="form-group form-md-floating-label">
                  <label>Single KOT</label><br>
                  <el-switch
                    v-model="settings.enable_single_kot"
                    active-color="#0c6"
                    inactive-color="#ff4949"
                    :active-value="1"
                    :inactive-value="0"
                    :disabled="upserting || (settings.id === undefined && !can('print_setting create')) ||
                      (settings.id !== undefined && !can('print_setting update'))"
                    active-text="Yes"
                    inactive-text="No"
                  />
                </div>
                <div class="form-group form-md-floating-label">
                  <label>One-slip-per-item KOT</label><br>
                  <el-switch
                    v-model="settings.enable_separate_slip_per_item_kot"
                    active-color="#0c6"
                    inactive-color="#ff4949"
                    :active-value="1"
                    :inactive-value="0"
                    :disabled="upserting || (settings.id === undefined && !can('print_setting create')) ||
                      (settings.id !== undefined && !can('print_setting update'))"
                    active-text="Yes"
                    inactive-text="No"
                  />
                </div>
                <div class="form-group form-md-floating-label">
                  <label>Tax receipts</label><br>
                  <el-switch
                    v-model="settings.enable_tax_receipt"
                    active-color="#0c6"
                    inactive-color="#ff4949"
                    :active-value="1"
                    :inactive-value="0"
                    :disabled="upserting || (settings.id === undefined && !can('print_setting create')) ||
                      (settings.id !== undefined && !can('print_setting update'))"
                    active-text="Yes"
                    inactive-text="No"
                  />
                </div>
                <div class="align-self-flex-end mt-2em">
                  <button
                    v-if="settings.id !== undefined && can('print_setting delete')"
                    class="btn btn-outline blue mr-1em"
                    type="button"
                    @click="displayDeleteModal()"
                  >
                    Delete
                  </button>
                  <button
                    v-if="
                      (settings.id === undefined && can('print_setting create')) ||
                        (settings.id !== undefined && can('print_setting update'))
                    "
                    class="btn blue align-self-flex-end"
                    :disabled="upserting"
                  >
                    {{ settings.id === undefined ? 'Create' : 'Save' }}
                    <i
                      v-show="upserting"
                      class="fa fa-spinner fa-pulse fa-fw"
                    />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- START DELETE -->
    <modal
      :show="showDeleteModal"
      effect="fade"
      @closeOnEscape="closeDeleteModal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        <button
          type="button"
          class="close"
          @click="closeDeleteModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Confirm Delete
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div class="row">
          <div class="col-md-12">
            <div
              v-show="deleteErrorMessage"
              ref="deleteErrorMessage"
              class="alert alert-danger"
            >
              <button
                class="close"
                @click="clearError('deleteErrorMessage')"
              />
              <span>{{ deleteErrorMessage }}</span>
            </div>
          </div>
        </div>
        <p>Are you sure you want to delete printer settings for {{ activeLocation.display_name }}?</p>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer clear"
      >
        <button
          type="button"
          class="btn blue"
          :disabled="deleting"
          @click="deleteSettings()"
        >
          Delete
          <i
            v-show="deleting"
            class="fa fa-spinner fa-pulse fa-fw"
          />
        </button>
      </div>
    </modal>
    <!-- END DELETE -->
  </div>
</template>

<script>
import Breadcrumb from '../../modules/Breadcrumb'
import Modal from '@/components/modules/Modal'
import NoResults from '@/components/modules/NoResults'
import PrinterSettingsFunctions from '@/controllers/PrinterSettings'
import LoadingScreen from '../../modules/LoadingScreen'
import ajaxErrorHandler from '@/controllers/ErrorController'
import { mapGetters } from 'vuex'

export default {
  components: {
    Breadcrumb,
    LoadingScreen,
    Modal,
    NoResults
  },
  data () {
    return {
      breadcrumbArray: [
        { name: 'Print', link: false },
        { name: 'Settings', link: false }
      ],

      loading: false,
      listErrorMessage: '',
      settings: {
        enable_single_kot: 0,
        enable_separate_slip_per_item_kot: 0,
        enable_tax_receipt: 0
      },
      upserting: false,

      showDeleteModal: false,
      deleting: false,
      deleteErrorMessage: ''
    }
  },
  computed: {
    activeLocationId: function () {
      return this.activeLocation.id
    },
    ...mapGetters(['can', 'activeLocation'])
  },
  watch: {
    activeLocationId: {
      handler: function (newId) {
        if (newId !== undefined) {
          this.clearError('listErrorMessage')
          this.getSettings()
        } else {
          this.clearSettings()
        }
      },
      immediate: true
    }
  },
  methods: {
    /**
     * To clear the current error.
     * @function
     * @param {string} name - Name of the error variable to clear
     * @returns {undefined}
     */
    clearError (name) {
      this[name] = ''
    },
    /**
     * To clear the current error.
     * @function
     * @param {string} name - Name of the error variable to clear
     * @returns {undefined}
     */
    clearSettings () {
      this.settings = {
        enable_single_kot: 0,
        enable_separate_slip_per_item_kot: 0,
        enable_tax_receipt: 0
      }
      this.original = {
        enenable_single_kot: 0,
        enable_separate_slip_per_item_kot: 0,
        enable_tax_receipt: 0
      }
    },

    /**
     * To get the printer settings.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getSettings () {
      this.loading = true
      var _this = this
      PrinterSettingsFunctions.getSettings(this.activeLocationId)
        .then(response => {
          if (response.payload.id === undefined) {
            this.clearSettings()
          } else {
            this.original = response.payload
            this.settings = response.payload
          }
        })
        .catch(reason => {
          _this.loading = false
          ajaxErrorHandler({
            reason,
            errorText: 'We could not fetch printer settings',
            errorName: 'listErrorMessage',
            vue: _this
          })
        })
        .finally(() => {
          _this.loading = false
        })
    },
    /**
     * To reset the create form.
     * @function
     * @returns {undefined}
     */
    reset () {
      this.settings = this.original
    },
    /**
     * To check if the data is valid before submitting to the backend.
     * @function
     * @returns {object} A promise that will validate the input form
     */
    validatePrinterData () {
      // var _this = this
      return new Promise(function (resolve, reject) {
        resolve('Hurray')
      })
    },
    /**
     * To save the settings.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    upsertSettings () {
      var _this = this
      _this.clearError('listErrorMessage')

      return _this
        .validatePrinterData()
        .then(response => {
          _this.upserting = true
          const payload = {
            ..._this.settings,
            location_id: this.activeLocation.id
          }
          PrinterSettingsFunctions.upsertSettings(payload)
            .then(response => {
              _this.original = response.payload
              _this.settings = response.payload
              _this.showUpsertSuccess(response.payload)
            })
            .catch(reason => {
              ajaxErrorHandler({
                reason,
                errorText: 'We could not save the settings',
                errorName: 'listErrorMessage',
                vue: _this
              })
            })
            .finally(() => {
              _this.upserting = false
            })
        })
        .catch(reason => {
          _this.listErrorMessage = reason
        })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showUpsertSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The printer settings have been saved'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The printer settings have been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To show the delete modal.
     * @function
     * @returns {undefined}
     */
    displayDeleteModal () {
      this.showDeleteModal = true
    },
    /**
     * To close the delete modal.
     * @function
     * @returns {undefined}
     */
    closeDeleteModal () {
      this.showDeleteModal = false
    },
    /**
     * To delete settings.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    deleteSettings () {
      this.clearError('deleteErrorMessage')
      let _this = this
      this.deleting = true
      PrinterSettingsFunctions.deleteSettings(this.settings.id)
        .then(response => {
          this.clearSettings()
          this.closeDeleteModal()
          this.showDeleteSuccess(response.payload)
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not remove the settings',
            errorName: 'deleteErrorMessage',
            vue: _this
          })
        })
        .finally(() => {
          this.deleting = false
        })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showDeleteSuccess (payload = {}) {
      let title = 'Success'
      let text = 'The printer settings have been removed'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The removal has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    }
  }
}
</script>
